import React, { useState, FormEvent } from 'react';
import axios from 'axios';
import { IMaskInput } from 'react-imask';
import { StyleguideComponentProps } from 'lib/component-props';
import { classNames } from 'assets/utils/helper';
import { getPldFormLogicAppUrl } from 'lib/util';

type PLDFormFields = {
  submitterName: string;
  submitterEmail: string;
  submitterRole: string;
  providerName: string;
  providerNPI: string;
  employmentStatus?: string;
  providerLocation: string;
  providerPhone: string;
  gender: string;
  addSpecialty: string;
  removeSpecialty: string;
  addLanguages: string;
  removeLanguages: string;
  addCertification: string;
  removeCertification: string;
  addTraining: string;
  removeTraining: string;
  incorrectLocation: string;
  incorrectAddress: string;
  incorrectPhone: string;
  incorrectOther: string;
  missingLocation: string;
  hideLocation: string;
  locationRank: string;
  locationPhone: string;
  locationOther: string;
  addFocusArea: string;
  removeFocusArea: string;
  focusApproval: string;
  accepting: string;
  biography: string;
  photo: File | null;
  comments: string;
  hideLocationOnCall?: string;
};

const initialState: PLDFormFields = {
  submitterName: '',
  submitterEmail: '',
  submitterRole: '',
  providerName: '',
  providerNPI: '',
  employmentStatus: '',
  providerLocation: '',
  providerPhone: '',
  gender: '',
  addSpecialty: '',
  removeSpecialty: '',
  addLanguages: '',
  removeLanguages: '',
  addCertification: '',
  removeCertification: '',
  addTraining: '',
  removeTraining: '',
  incorrectLocation: '',
  incorrectAddress: '',
  incorrectPhone: '',
  incorrectOther: '',
  missingLocation: '',
  hideLocation: '',
  locationRank: '',
  locationPhone: '',
  locationOther: '',
  addFocusArea: '',
  removeFocusArea: '',
  focusApproval: '',
  accepting: '',
  biography: '',
  photo: null,
  comments: '',
  hideLocationOnCall: '',
};

const failedMsg =
  'Sorry, something went wrong with the submission. Please try again.';

const PLDForm = (props: StyleguideComponentProps) => {
  const [formFields, setFormFields] = useState({ ...initialState });
  const [validEmail, setValidEmail] = useState<boolean | null>(null);
  const [validPhone, setValidPhone] = useState<boolean | null>(null);
  const [validImage, setValidImage] = useState<boolean | null>(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [showing, setShowing] = useState(false);
  const [multipleProviders, setMultipleProviders] = useState<boolean | null>(
    null
  );
  const [hideLocationOnCallError, setHideLocationOnCallError] = useState('');
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const logicAppUrl = getPldFormLogicAppUrl();

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;

    setInvalidFields(prev => {
      const updated = new Set(prev);
      updated.add(name);
      return updated;
    });
  };

  const clearInvalidField = (name: string) => {
    setInvalidFields(prev => {
      const updated = new Set(prev);
      updated.delete(name);
      return updated;
    });
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    clearInvalidField(name);

    if (name === 'submitterEmail') {
      setValidEmail(null);
    }

    if (value.length === 0) {
      e.target
        .closest('.field-wrapper')
        ?.querySelector('.error-msg')
        ?.classList.remove('show');
    }
    setFormFields(prevFields => ({
      ...prevFields,
      [name]: value,
    }));
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    clearInvalidField(name);

    setFormFields(prevFields => ({
      ...prevFields,
      [name]: checked ? 'true' : 'false',
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUpload = e.target.files?.[0];
    const img = new Image();
    img.src = (fileUpload && URL.createObjectURL(fileUpload)) || '';
    img.onload = function () {
      if (!(img.width >= 1200) || !(img.height >= 1200)) {
        setValidImage(false);
      } else {
        setValidImage(true);
        setResponseMsg('');
      }
      fileUpload &&
        setFormFields(prevFields => ({
          ...prevFields,
          photo: fileUpload,
        }));
    };
  };

  const handleNumProvidersChange = (isMultiple: boolean) => {
    clearInvalidField('num-providers');
    clearInvalidField('providerLocation');

    setMultipleProviders(isMultiple);
    setFormFields(prevFields => ({
      ...prevFields,
      providerName: '',
      providerNPI: '',
      providerLocation: '',
    }));
    clearInvalidField('providerName');
    clearInvalidField('providerNPI');
    (document.getElementById('singleIlProvider') as HTMLInputElement).checked =
      false;
    (document.getElementById('singleMiProvider') as HTMLInputElement).checked =
      false;
    (
      document.getElementById('multipleIlProvider') as HTMLInputElement
    ).checked = false;
    (
      document.getElementById('multipleMiProvider') as HTMLInputElement
    ).checked = false;
  };

  const clearImg = (input: HTMLInputElement) => {
    input.value = '';
    setValidImage(null);
    setFormFields(prevFields => ({
      ...prevFields,
      photo: null,
    }));
    setResponseMsg('');
  };

  const validateEmail = () => {
    if (formFields.submitterEmail.endsWith('@osfhealthcare.org')) {
      setValidEmail(true);
      clearInvalidField('submitterEmail');
      setResponseMsg('');
    } else {
      setValidEmail(false);
      setInvalidFields(prev => {
        const updated = new Set(prev);
        updated.add('submitterEmail');
        return updated;
      });
    }
  };

  const toggleField = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const toggleBtn = e.target as HTMLElement;
    toggleBtn.closest('.expandable-field')?.classList.toggle('show');
  };

  const handleDeselectableRadio = (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => {
    const { name, value } = e.currentTarget;

    clearInvalidField(name);

    if (name === 'hideLocationOnCall') {
      setHideLocationOnCallError('');
    }
    setFormFields(prev => {
      const current = (prev as any)[name] as string;
      return {
        ...prev,
        [name]: current === value ? '' : value,
      };
    });
  };

  const noop = () => {};

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (validPhone === false) {
      setResponseMsg('Please provide a valid phone number.');
      return;
    }
    if (
      formFields.hideLocation.trim().length > 0 &&
      formFields.hideLocationOnCall !== 'Yes' &&
      formFields.hideLocationOnCall !== 'No'
    ) {
      setHideLocationOnCallError(
        'Please check Yes or No for on call coverage.'
      );

      setInvalidFields(prev => {
        const updated = new Set(prev);
        updated.add('hideLocationOnCall');
        return updated;
      });

      setResponseMsg('');
      return;
    }
    if (validEmail && validImage !== false) {
      let photoData = null;
      if (formFields.photo) {
        const base64Content = await new Promise<string>(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.readAsDataURL(formFields.photo!);
        });

        photoData = {
          filename: formFields.photo.name,
          contentType: formFields.photo.type,
          content: base64Content,
        };
      }

      const { photo, ...otherValues } = formFields;
      const formData = {
        ...otherValues,
        ...(photoData && { photo: photoData }),
      };

      if (formData.providerName.includes('\n')) {
        formData.providerName = formData.providerName
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\r?\n/g, '<br>');
      }

      setResponseMsg(
        'Thank you. Working on submitting...Please wait for confirmation.'
      );

      axios
        .post(logicAppUrl, formData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(function () {
          window.location.href = '/provider-update/success';
        })
        .catch(function (error) {
          console.error('Submission error:', error);
          setResponseMsg(failedMsg);
        });
    } else if (!validEmail) {
      setResponseMsg('Please verify your email address.');
    } else {
      setResponseMsg('Please update or remove the added photo.');
    }
  };

  return (
    <div className="overhauled" id={`pld-form-${props.rendering?.uid}`}>
      <div className="jss-inner-container pld-form outer-vertical-spacing">
        <h1 className="title">Provider Update Request Form</h1>
        <p className="osf-intro-copy bottom-margin">
          Provider data from various sources is displayed on osfhealthcare.org
          in the provider directory. Use this form to request corrections to a
          provider's directory profile. For changes to <em>Accepting New Patients</em> for OSFMG Primary Care providers, please submit your request through the centralized template analyst. For other providers, request the change through the form below.
        </p>
        <div className={classNames('form-start', showing ? 'hidden' : '')}>
          <h2 className="osf-display-h2 text-center bottom-margin-sm">
            Select an option below to begin.
          </h2>
          <div className="form-options">
            <button
              className="form-option btn-secondary no-icon"
              onClick={() => setShowing(true)}
            >
              Provider has errors on their profile.
              <p className="description">
                Includes hiding/showing locations on a provider profile.
              </p>
            </button>

            <a
              href="https://forms.office.com/r/kx51PaMacA"
              className="form-option btn-secondary no-icon"
              target="_blank"
            >
              Provider is no longer practicing at OSF HealthCare.
            </a>
            <a
              href="https://forms.office.com/r/AewHMcKRqs"
              className="form-option btn-secondary no-icon"
              target="_blank"
            >
              Provider office/department addition or removal within Epic.
            </a>
          </div>
        </div>
        <div className={classNames('form-wrapper', showing ? 'shown' : '')}>
          <button
            className="btn-tertiary back-btn"
            onClick={() => setShowing(false)}
          >
            Back
          </button>
          <form onSubmit={handleSubmit} id="pld-form">
            <em>
              <span className="required">*</span> indicates required field
            </em>
            <h3>Submitter Details</h3>
            <fieldset>
              <div className="field-wrapper">
                <label htmlFor="submitterName">
                  <span className="required">*</span>Submitter Name
                </label>
                <input
                  id="submitterName"
                  type="text"
                  placeholder="Ex: John Doe"
                  className='input-text-box'
                  name="submitterName"
                  value={formFields.submitterName}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  aria-invalid={invalidFields.has('submitterName')}
                  required
                />
              </div>
              <div className="field-wrapper">
                <label htmlFor="submitterEmail">
                  <span className="required">*</span>Submitter Email
                  <em> - Please use your OSF email.</em>
                </label>
                <input
                  id="submitterEmail"
                  type="text"
                  placeholder="Ex: john.doe@osfhealthcare.org"
                  className='input-text-box'
                  name="submitterEmail"
                  value={formFields.submitterEmail}
                  onBlur={validateEmail}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  aria-invalid={
                    validEmail === false ||
                    invalidFields.has('submitterEmail')
                  }
                  required
                />
                <em
                  className={
                    validEmail == false
                      ? 'error-msg show osf-label'
                      : 'error-msg osf-label'
                  }
                >
                  Please use your OSF email.
                </em>
              </div>
              <div className="field-wrapper">
                <label htmlFor="submitterRole">
                  <span className="required">*</span>Submitter Role
                </label>
                <input
                  id="submitterRole"
                  type="text"
                  name="submitterRole"
                  className='input-text-box'
                  value={formFields.submitterRole}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  aria-invalid={invalidFields.has('submitterRole')}
                  required
                />
              </div>
            </fieldset>

            <h3>Provider Information</h3>
            <fieldset>
              <p className="num-heading">
                <strong>
                  <span className="required">*</span>These changes apply to
                </strong>
              </p>
              <input
                type="radio"
                name="num-providers"
                id="single"
                value="single"
                required
                onInvalid={handleInvalid}
                aria-invalid={invalidFields.has('num-providers')}
                onClick={() => handleNumProvidersChange(false)}
              />

              <label htmlFor="single">Only one provider</label>

              <input
                type="radio"
                name="num-providers"
                id="multiple"
                value="multiple"
                required
                onInvalid={handleInvalid}
                aria-invalid={invalidFields.has('num-providers')}
                onClick={() => handleNumProvidersChange(true)}
              />

              <label htmlFor="multiple">Multiple providers</label>
            </fieldset>
            <fieldset
              className={classNames(
                'single-provider-info',
                multipleProviders === false ? 'shown' : ''
              )}
            >
              <div className="field-wrapper">
                <label htmlFor="providerName">
                  <span className="required">*</span>Provider Name
                </label>

                <input
                  id="providerName"
                  type="text"
                  placeholder="Ex: John Doe"
                  className='input-text-box'
                  name="providerName"
                  value={formFields.providerName}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  aria-invalid={invalidFields.has('providerName')}
                  {...(multipleProviders === false ? { required: true } : {})}
                />
              </div>

              <div className="field-wrapper">
                <label htmlFor="providerNPI">
                  <span className="required">*</span>Provider NPI
                </label>

                <input
                  id="providerNPI"
                  type="text"
                  name="providerNPI"
                  className='input-text-box'
                  value={formFields.providerNPI}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  aria-invalid={invalidFields.has('providerNPI')}
                  {...(multipleProviders === false ? { required: true } : {})}
                />
              </div>

              <div className="field-wrapper">
                <label>
                  <span className="required">*</span>Provider Location
                </label>

                <div>
                  <input
                    id="singleIlProvider"
                    type="radio"
                    name="providerLocation"
                    value="IL Provider"
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    aria-invalid={invalidFields.has('providerLocation')}
                    {...(multipleProviders === false ? { required: true } : {})}
                  />

                  <label htmlFor="singleIlProvider" className="radio-label">
                    IL Provider
                  </label>

                  <input
                    id="singleMiProvider"
                    type="radio"
                    name="providerLocation"
                    value="MI Provider"
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    aria-invalid={invalidFields.has('providerLocation')}
                    {...(multipleProviders === false ? { required: true } : {})}
                  />

                  <label htmlFor="singleMiProvider" className="radio-label">
                    MI Provider
                  </label>
                </div>
              </div>
            </fieldset>

            <fieldset
              className={classNames(
                'multiple-provider-info',
                multipleProviders ? 'shown' : ''
              )}
            >
              <div className="field-wrapper">
                <label htmlFor="providerName">
                  <span className="required">*</span>Provider Names and NPIs
                </label>
                <textarea
                  id="providerName"
                  placeholder={`List each provider name and NPI on a new line\nEx: John Doe - 1234567890`}
                  name="providerName"
                  value={formFields.providerName}
                  onChange={handleChange}
                  {...(multipleProviders ? { required: true } : {})}
                />
              </div>
              <div className="field-wrapper">
                <label>
                  <span className="required">*</span>Provider Location
                </label>
                <div>
                  <input
                    id="multipleIlProvider"
                    type="radio"
                    name="providerLocation"
                    value="IL Provider"
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    aria-invalid={invalidFields.has('providerLocation')}
                    {...(multipleProviders ? { required: true } : {})}
                  />
                  <label htmlFor="multipleIlProvider" className="radio-label">
                    IL Provider
                  </label>
                  <input
                    id="multipleMiProvider"
                    type="radio"
                    name="providerLocation"
                    value="MI Provider"
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    aria-invalid={invalidFields.has('providerLocation')}
                    {...(multipleProviders ? { required: true } : {})}
                  />
                  <label htmlFor="multipleMiProvider" className="radio-label">
                    MI Provider
                  </label>
                </div>
              </div>
            </fieldset>
            <h3>Personal Information</h3>
            <p className="instructions">
              Name changes can only take place if the medical license is up to
              date and must follow our standard{' '}
              <a href="https://osfprod.service-now.com/esc?id=sc_cat_item&table=sc_cat_item&sys_id=19c51805dbcef410339f6f13ca961996&searchTerm=name%20change">
                HR name change process
              </a>
              . Any updates in this section must match the provider&apos;s
              medical license.
            </p>
            <div className="location-wrapper">
              <div className="expandable-field">
                <label>
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Demographics
                  </button>
                </label>
                <div className="field-wrapper">
                  <label htmlFor="providerPhone">Phone Number</label>

                  <IMaskInput
                    id="providerPhone"
                    mask="000-000-0000"
                    unmask={true}
                    value={formFields.providerPhone}
                    onAccept={value => {
                      setValidPhone(null);

                      setFormFields(prev => ({
                        ...prev,
                        providerPhone: value,
                      }));
                    }}
                    placeholder="Ex: ###-###-####"
                    className='input-text-box'
                    name="providerPhone"
                    type="text"
                    aria-invalid={validPhone === false}
                    onBlur={e => {
                      const raw = e.target.value.trim();
                      if (!raw) {
                        setValidPhone(null);
                        return;
                      }
                      const digits = raw.replace(/\D/g, '');
                      setValidPhone(digits.length === 10);
                    }}
                  />
                  <em
                    className={
                      validPhone == false
                        ? 'error-msg show osf-label'
                        : 'error-msg osf-label'
                    }
                  >
                    Please provide a valid phone number in the provided format.
                  </em>

                  <label>Gender</label>
                  <div>
                    <input
                      id="male"
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={formFields.gender === 'Male'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="male" className="radio-label">
                      Male
                    </label>
                    <input
                      id="female"
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={formFields.gender === 'Female'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="female" className="radio-label">
                      Female
                    </label>
                  </div>

                  <label>Employment Status</label>
                  <div>
                    <input
                      id="employed"
                      type="radio"
                      name="employmentStatus"
                      value="Employed"
                      checked={formFields.employmentStatus === 'Employed'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />

                    <label htmlFor="employed" className="radio-label">
                      Employed
                    </label>
                    <input
                      id="contracted"
                      type="radio"
                      name="employmentStatus"
                      value="Contracted"
                      checked={formFields.employmentStatus === 'Contracted'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="contracted" className="radio-label">
                      Contracted
                    </label>
                    <input
                      id="other"
                      type="radio"
                      name="employmentStatus"
                      value="Other"
                      checked={formFields.employmentStatus === 'Other'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="other" className="radio-label">
                      Other
                    </label>
                  </div>

                  <label>
                    Accepting New Patients -{' '}
                    <em>
                      Note, the accepting new patients flag is not department
                      specific and if a provider is accepting new patients at
                      at-least one location, this should be set to Yes.
                    </em>
                  </label>
                  <div>
                    <input
                      id="isAccepting"
                      type="radio"
                      name="accepting"
                      value="Yes"
                      checked={formFields.accepting === 'Yes'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="isAccepting" className="radio-label">
                      Yes
                    </label>
                    <input
                      id="notAccepting"
                      type="radio"
                      name="accepting"
                      value="No"
                      checked={formFields.accepting === 'No'}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label htmlFor="notAccepting" className="radio-label">
                      No
                    </label>
                  </div>
                </div>
              </div>

              <div className="expandable-field">
                <label>
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Specialties
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    Specialties are based on official Board Certifications. They
                    can only be changed if a new certification has been
                    achieved, lapsed, or if the listed certifications are
                    incorrect.
                  </p>
                  <label htmlFor="addSpecialty">Add Specialty</label>
                  <input
                    id="addSpecialty"
                    type="text"
                    placeholder="Ex: Family Medicine"
                    className='input-text-box'
                    name="addSpecialty"
                    value={formFields.addSpecialty}
                    onChange={handleChange}
                  />

                  <label htmlFor="removeSpecialty">Remove Specialty</label>
                  <input
                    id="removeSpecialty"
                    type="text"
                    placeholder="Ex: Family Medicine"
                    className='input-text-box'
                    name="removeSpecialty"
                    value={formFields.removeSpecialty}
                    onChange={handleChange}
                  />
                </div>
                <div className="field-wrapper">
                  <p className="osf-label">
                    The Clinical Area of Focus is meant to supplement listed
                    Specialties. Examples include adding a focus area to a
                    Family Nurse Practitioner who practices Cardiology, or to
                    reflect that doctor certified in General Surgery frequently
                    performs Breast Surgery. <br />- No more than three Clinical
                    Areas of Focus. <br />- Conditions and treatments do not
                    qualify for Clinical Area of Focus.
                  </p>
                  <label htmlFor="addFocusArea">Add Clinical Focus Area</label>
                  <textarea
                    id="addFocusArea"
                    name="addFocusArea"
                    value={formFields.addFocusArea}
                    onChange={handleChange}
                    placeholder="Ex: Internal Medicine"
                  />
                  <label htmlFor="removeFocusArea">
                    Remove Clinical Focus Area
                  </label>
                  <textarea
                    id="removeFocusArea"
                    name="removeFocusArea"
                    value={formFields.removeFocusArea}
                    onChange={handleChange}
                    placeholder="Ex: Internal Medicine"
                  />
                  {(formFields.addFocusArea.length > 0 ||
                    formFields.removeFocusArea.length > 0) && (
                    <span className="required">*</span>
                  )}

                  <input
                    type="checkbox"
                    id="focusApproval"
                    name="focusApproval"
                    onChange={handleCheck}
                    onInvalid={handleInvalid}
                    aria-invalid={invalidFields.has('focusApproval')}
                    checked={formFields.focusApproval == 'true'}
                    required={
                      formFields.addFocusArea.length > 0 ||
                      formFields.removeFocusArea.length > 0
                    }
                  />
                  <label htmlFor="focusApproval">
                    Clinical focus is clinically appropriate for provider or has
                    been reviewed and approved by managing medical director.
                  </label>
                </div>
              </div>

              <div className="expandable-field">
                <label>
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Languages
                  </button>
                </label>

                <div className="field-wrapper">
                  <label htmlFor="addLanguages">Add Language</label>
                  <input
                    id="addLanguages"
                    type="text"
                    placeholder="Ex: English"
                    className='input-text-box'
                    name="addLanguages"
                    value={formFields.addLanguages}
                    onChange={handleChange}
                  />

                  <label htmlFor="removeLanguages">Remove Language</label>

                  <input
                    id="removeLanguages"
                    type="text"
                    placeholder="Ex: English"
                    className='input-text-box'
                    name="removeLanguages"
                    value={formFields.removeLanguages}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="expandable-field">
                <label>
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Education / Training/ Certification
                  </button>
                </label>
                <div className="field-wrapper">
                  <label htmlFor="addCertification">
                    Add Board Certification
                  </label>
                  <input
                    id="addCertification"
                    type="text"
                    placeholder="Ex: Family Nurse Practitioner - American Academy of Nurse Practitioners, 2015"
                    className='input-text-box'
                    name="addCertification"
                    value={formFields.addCertification}
                    onChange={handleChange}
                  />

                  <label htmlFor="removeCertification">
                    Remove Board Certification
                  </label>
                  <input
                    id="removeCertification"
                    type="text"
                    placeholder="Ex: Family Nurse Practitioner - American Academy of Nurse Practitioners, 2015"
                    className='input-text-box'
                    name="removeCertification"
                    value={formFields.removeCertification}
                    onChange={handleChange}
                  />

                  <label htmlFor="addTraining">Add Training/Education</label>
                  <input
                    id="addTraining"
                    type="text"
                    placeholder="Ex: OSF Saint Francis Medical Center - Fellowship, 2015"
                    className='input-text-box'
                    name="addTraining"
                    value={formFields.addTraining}
                    onChange={handleChange}
                  />

                  <label htmlFor="removeTraining">
                    Remove Training/Education
                  </label>
                  <input
                    id="removeTraining"
                    type="text"
                    placeholder="Ex: OSF Saint Francis Medical Center - Fellowship, 2015"
                    className='input-text-box'
                    name="removeTraining"
                    value={formFields.removeTraining}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <h3>Location Information</h3>
            <p className="instructions">
              Use this form to report incorrect or missing location details. If
              the location is an Epic department, provide the Epic Department ID
              in your details.
            </p>

            <div className="location-wrapper">
              <div className="expandable-field">
                <label htmlFor="incorrectLocation">
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Incorrect Location Details
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    List the location name and{' '}
                    <strong>Epic Department ID</strong> that has incorrect
                    information.
                  </p>
                  <textarea
                    id="incorrectLocation"
                    name="incorrectLocation"
                    onChange={handleChange}
                    value={formFields.incorrectLocation}
                    placeholder={
                      'If more than one office, list each on a new line. \n1. Office name #1 - 1234567890 \n2. Office name #2 - 0987654321 \n3. ...'
                    }
                  />
                </div>

                <div className="field-wrapper">
                  <p className="osf-label">
                    If the <strong>address</strong> is incorrect, list the
                    correct information below. Note any offices with correct
                    information.
                  </p>
                  <textarea
                    id="incorrectAddress"
                    name="incorrectAddress"
                    onChange={handleChange}
                    value={formFields.incorrectAddress}
                    placeholder={
                      'Follow the same order as above. \n1. 124 SW Adams St. \n2. Correct \n3. ...'
                    }
                  />
                </div>

                <div className="field-wrapper">
                  <p className="osf-label">
                    If the <strong>phone number</strong> is incorrect, list the
                    correct information below. Note any offices with correct
                    information.
                  </p>
                  <textarea
                    id="incorrectPhone"
                    name="incorrectPhone"
                    onChange={handleChange}
                    value={formFields.incorrectPhone}
                    placeholder={
                      'Follow the same order as above. \n1. Correct \n2. (309) 123-4567 \n3. ...'
                    }
                  />
                </div>

                <div className="field-wrapper">
                  <p className="osf-label">
                    If something else is incorrect, please note it below.
                  </p>
                  <textarea
                    id="incorrectOther"
                    name="incorrectOther"
                    onChange={handleChange}
                    value={formFields.incorrectOther}
                    placeholder={'Follow the same order as above.'}
                  />
                </div>
              </div>

              <div className="expandable-field">
                <label htmlFor="missingLocation">
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Location Missing from Public Directory
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    Use this field for locations that are correctly assigned in
                    Epic, but are not appearing on the public provider
                    directory. Do NOT use this form for adding new locations to
                    a provider profile. Include the{' '}
                    <strong>Epic Dept ID</strong> in the details below.
                  </p>
                  <textarea
                    id="missingLocation"
                    name="missingLocation"
                    onChange={handleChange}
                    value={formFields.missingLocation}
                    placeholder={
                      'OSF Medical Group - Internal Medicine \nEpic Dept ID: 102673938'
                    }
                  />
                </div>
              </div>
              <div className="expandable-field">
                <label htmlFor="hideLocation">
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Hide a Location
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    Enter the location that should not be publicly visible. The
                    existing location(s) will be retained for billing and on
                    call scheduling purposes. Include the location name and{' '}
                    <strong>Epic Department ID</strong> to ensure the correct
                    changes are applied.
                  </p>
                  <textarea
                    id="hideLocation"
                    name="hideLocation"
                    onChange={handleChange}
                    value={formFields.hideLocation}
                  />

                  <p className="osf-label">
                    {formFields.hideLocation.trim().length > 0 && (
                      <span className="required">*</span>
                    )}
                    Is this provider covering or on call at the location you're
                    asking to remove?
                  </p>

                  <div>
                    <input
                      id="hideLocationOnCallYes"
                      type="checkbox"
                      name="hideLocationOnCall"
                      value="Yes"
                      checked={formFields.hideLocationOnCall === 'Yes'}
                      aria-invalid={invalidFields.has('hideLocationOnCall')}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                    />
                    <label
                      htmlFor="hideLocationOnCallYes"
                      className="radio-label"
                    >
                      Yes
                    </label>

                    <input
                      id="hideLocationOnCallNo"
                      type="checkbox"
                      name="hideLocationOnCall"
                      value="No"
                      checked={formFields.hideLocationOnCall === 'No'}
                      aria-invalid={invalidFields.has('hideLocationOnCall')}
                      onClick={handleDeselectableRadio}
                      onChange={noop}
                      style={{ marginLeft: '20px' }}
                    />
                    <label
                      htmlFor="hideLocationOnCallNo"
                      className="radio-label"
                    >
                      No
                    </label>

                    {hideLocationOnCallError && (
                      <em className="error-msg show osf-label">
                        {hideLocationOnCallError}
                      </em>
                    )}
                  </div>
                </div>
              </div>

              <div className="expandable-field">
                <label htmlFor="locationRank">
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Incorrect Location Rank
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    Enter the locations and their correct order. Include the
                    location name and <strong>Epic Department ID</strong> to
                    ensure the correct changes are applied.
                  </p>
                  <textarea
                    id="locationRank"
                    name="locationRank"
                    onChange={handleChange}
                    value={formFields.locationRank}
                  />
                </div>
              </div>
              {/*
              <div className="expandable-field">
                <label htmlFor="locationOther">
                  <button
                    className="btn-secondary"
                    onClick={(e: any) => {
                      toggleField(e);
                    }}
                  >
                    Other
                  </button>
                </label>
                <div className="field-wrapper">
                  <p className="osf-label">
                    Enter any other comments or details related to your request.
                    Include the location name and <strong>Epic Department ID</strong> to ensure
                    the correct changes are applied.
                  </p>
                  <textarea
                    id="locationOther"
                    name="locationOther"
                    onChange={handleChange}
                    value={formFields.locationOther}
                  />
                </div>
              </div>
              */}
            </div>

            <h3>Marketable Information</h3>
            <p className="instructions">
              For any field you want to update, enter the new information that
              should be in the profile.
            </p>
            <div className="field-wrapper bio">
              <label htmlFor="biography">Biography</label>

              <p className="osf-label">
                This field is for Biography updates only. Do not add general
                comments here. Please find the appropriate field above to ensure
                your request is routed to the correct team.
              </p>
              <textarea
                id="biography"
                name="biography"
                value={formFields.biography}
                onChange={handleChange}
                placeholder="Ex: I treat all patients with kindness and respect. It is important to me to provide personal education as well, so they feel informed and comfortable making decisions about their health care."
              />
            </div>

            <div className="field-wrapper">
              <label htmlFor="photo">
                Photo Attachment -{' '}
                <em>Photo must be at least 1200x1200 pixels</em>
              </label>
              <input
                id="photo"
                type="file"
                name="photo"
                onChange={handleImageChange}
                accept="image/*"
              />
              <button
                type="button"
                className={`${
                  validImage !== null ? 'shown' : ''
                } remove-btn btn-tertiary`}
                onClick={() =>
                  clearImg(document.getElementById('photo') as HTMLInputElement)
                }
              >
                Remove
              </button>
              <em
                className={
                  validImage == false
                    ? 'error-msg show osf-label'
                    : 'error-msg osf-label'
                }
              >
                Photo must be at least 1200x1200 pixels.
              </em>
            </div>

            <div className="btns-wrapper">
              <button type="submit" className="btn-primary submit-btn">
                Submit
              </button>
            </div>
          </form>
          <div>
            <h2 className="response-msg">{responseMsg}</h2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLDForm;
